import React, { useEffect, useMemo, useState } from 'react';
import { pixelsToDataUrl } from '../lib/pixelsToDataUrl';
import { PICO8_PALETTE } from '../lib/pico8';
import { PICO8_SECONDARY } from '../utils/palette';

interface AITabProps {
  description: string;
  gridSize: number;
  useSecondaryPalette: boolean;
  isLoading: boolean;
  error: string | null;
  onDescriptionChange: (value: string) => void;
  onGridSizeChange: (size: number) => void;
  onSecondaryPaletteChange: (value: boolean) => void;
  onGenerate: () => void;
  generatedImages: number[][][];
  isGenerating: boolean;
  generationStatus: string | null;
  onSelectSprite: (pixels: number[][]) => void;
}

const AITab: React.FC<AITabProps> = ({
  description,
  gridSize,
  useSecondaryPalette,
  isLoading,
  error,
  onDescriptionChange,
  onGridSizeChange,
  onSecondaryPaletteChange,
  onGenerate,
  generatedImages,
  isGenerating,
  generationStatus,
  onSelectSprite
}) => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (!isGenerating) {
      setElapsedSeconds(0);
      return;
    }
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(interval);
  }, [isGenerating]);

  const elapsedLabel = elapsedSeconds < 60
    ? `${elapsedSeconds}s`
    : `${Math.floor(elapsedSeconds / 60)}m ${elapsedSeconds % 60}s`;

  return (
    <div className="ai-tab">
      <div className="ai-form">
        <h3>AI Sprite Generator</h3>
        <p className="ai-generation-note">
          The first image may take longer while the AI worker starts. Following images are usually faster.
        </p>
        
        <div className="input-group">
          <label htmlFor="description">Description:</label>
          <input
            id="description"
            type="text"
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder="yellow duck with blue eyes"
            disabled={isLoading}
          />
        </div>

        <div className="input-group">
          <label>Sprite Size:</label>
          <div className="size-toggle">
            <button
              className={`tool-button ${gridSize === 8 ? 'active' : ''}`}
              onClick={() => onGridSizeChange(8)}
              disabled={isLoading}
            >
              8×8
            </button>
            <button
              className={`tool-button ${gridSize === 16 ? 'active' : ''}`}
              onClick={() => onGridSizeChange(16)}
              disabled={isLoading}
            >
              16×16
            </button>
          </div>
        </div>

        <div className="checkbox-group">
          <label>
            <input
              type="checkbox"
              checked={useSecondaryPalette}
              onChange={(e) => onSecondaryPaletteChange(e.target.checked)}
              disabled={isLoading}
            />
            <span>Use Extended Palette</span>
          </label>
          <p className="checkbox-help">Adds 16 extra colors for more variety</p>
        </div>

        <button 
          onClick={onGenerate}
          disabled={isLoading || !description.trim()}
          className="tool-button"
        >
          {isLoading ? 'Generating...' : 'Generate Sprite'}
        </button>

        {isGenerating && generationStatus && (
          <div className="generation-status" role="status" aria-live="polite">
            <span className="generation-status-spinner" aria-hidden="true" />
            <span className="generation-status-copy">
              <strong>{generationStatus}</strong>
            </span>
            <span className="generation-status-time" aria-hidden="true">{elapsedLabel}</span>
          </div>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {(generatedImages.length > 0 || isGenerating) && (
          <div className="generated-sprites">
            <h3>Generated sprite</h3>
            <GeneratedSpritesGrid 
              generatedImages={generatedImages}
              isGenerating={isGenerating}
              onSelectSprite={onSelectSprite}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Separate memoized component for sprite grid to prevent re-computation of dataUrls
interface GeneratedSpritesGridProps {
  generatedImages: number[][][];
  isGenerating: boolean;
  onSelectSprite: (pixels: number[][]) => void;
}

const GeneratedSpritesGrid: React.FC<GeneratedSpritesGridProps> = React.memo(({ 
  generatedImages, 
  isGenerating, 
  onSelectSprite 
}) => {
  // Memoize dataUrl conversion - only recompute when images or palette changes
  const imageDataUrls = useMemo(() => {
    return generatedImages.map((pixels, idx) => {
      try {
        const dataUrl = pixelsToDataUrl(pixels, PICO8_PALETTE, 128, PICO8_SECONDARY);
        return dataUrl || null;
      } catch (err) {
        console.error(`Error converting image ${idx}:`, err);
        return null;
      }
    });
  }, [generatedImages]);

  return (
    <div className="sprite-grid-2col">
      {/* Render all completed images */}
      {generatedImages.map((pixels, idx) => {
        const dataUrl = imageDataUrls[idx];
        if (!dataUrl) return null;
        
        return (
          <div
            key={`img-${idx}`}
            className="sprite-preview-btn"
            onClick={() => onSelectSprite(pixels)}
            title="Click to open in editor"
          >
            <img
              src={dataUrl}
              alt={`Sprite ${idx + 1}`}
              style={{ imageRendering: 'pixelated', width: '100%', height: '100%' }}
            />
          </div>
        );
      })}

      {/* Show loading placeholders while generating */}
      {isGenerating && (
        <div
          className="sprite-preview-btn"
          style={{ opacity: 0.5 }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, #ddd 25%, #eee 50%, #ddd 75%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s infinite',
            }}
          />
        </div>
      )}
    </div>
  );
});

GeneratedSpritesGrid.displayName = 'GeneratedSpritesGrid';

export default AITab;
